import React, { useRef, useEffect, useCallback, useState, ForwardRefRenderFunction, useImperativeHandle, forwardRef } from 'react';
import styled from 'styled-components/macro';
import { ResourceStrings } from '../../ResourceStrings';
import { Icon } from '@fluentui/react/lib/Icon';
import { showIf } from '../../utils/react';
import { getCurrentLineText, MarkdownElementType, Minimark } from '../../editor/Minimark';
import { useId } from '@fluentui/react-hooks';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import FileUploadDialog from './FileUpload/FileUploadDialog';
import { UploadedFileInfo, uploadFiles } from './FileUpload/FileUpload';
import { getCaretCoordinates, resetMirrorDiv } from './textAreaCaret';
import PersonPicker, { Person, PersonPickerHandles } from './PersonPicker';
import KeyCode from '../../editor/KeyCode';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import * as _ from "lodash";
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Operation, UIOperationState } from '../../store/operation';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootState';
import HtmlPreview from './HtmlPreview';

const defaultTextEditorMinHeightPx: number = 140;
const defaultTextEditorMaxHeightPx: number = 450;
const personPickerWidthtPx: number = 300;

export interface MarkdownEditorHandles {
    focus: () => void;
}

export interface MarkdownEditorProps {
    nodeTrackingId?: string;
    nodeAnswerHubId?: number;
    text: string;
    placeHolder?: string;
    textEditorMinHeightPx?: number;
    textEditorMaxHeightPx?: number;
    textAreaClassName?: string;
    disabled?: boolean;
    ariaLabel?: string;
    ariaRequired?: boolean;
    ariaDescribedby?: string;
    forbidAttachFiles?: boolean;
    reasonCantAttachFiles?: string;
    isInvalid?: boolean;
    notificationText?: string;
    relevantPersons?: Person[];
    className?: string;
    searchPersons?: (filter: string) => Person[];
    formatInfoHandler: (action: string) => void;
    beforePreview?: ((markdown: string) => string);
    textChanged: (value: string) => void;
    setDirty?: () => void;
    setRefresh?: (refresh: () => void) => void;
    onFileAttached?: () => void;
}

const MarkdownEditor: ForwardRefRenderFunction<MarkdownEditorHandles, MarkdownEditorProps> = (props: MarkdownEditorProps, ref): JSX.Element => {
    const refPreview = useRef<HTMLDivElement>(null);
    const refTextEditor = useRef<HTMLTextAreaElement>(null);
    const refPersonPickerContainer = useRef<HTMLDivElement>(null);
    const refPersonPicker = useRef<PersonPickerHandles>(null);
    const errorMesageId = useId('reportDetail-messages');
    const [showFileUploadDialog, setShowFileUploadDialog] = useState(false);
    const [currentActivity, setCurrentActivity] = useState<Operation>(null);
    const [attachImage, setAttachImage] = useState(true);
    const [previewHtml, setPreviewHtml] = useState<string>();
    const [markdownSuggestion, setMarkdownSuggestion] = useState<{
        elementType: MarkdownElementType;
        selectionStart: number;
        selectionEnd: number;
        filterText: string;
    }>(null);

    const isInReadOnlyMode = useSelector((state: RootState) => state.appState.isInReadOnlyMode);
    const approvedFileExtensionsCsv = useSelector((state: RootState) => state.appState.approvedFileExtensionsCsv);

    const refMarkdownSuggestion = useRef<any>();
    useEffect(() => { refMarkdownSuggestion.current = markdownSuggestion });

    const textEditorMinHeightPx = props.textEditorMinHeightPx ?? defaultTextEditorMinHeightPx;
    const textEditorMaxHeightPx = props.textEditorMaxHeightPx ?? defaultTextEditorMaxHeightPx;

    const refMarkdownEditor = useRef<Minimark>(null);

    useImperativeHandle(ref, () => ({
        focus: () => {
            refMarkdownEditor.current?.focus();
        }
    }));

    useEffect(() => {
        if (refTextEditor.current) {
            refMarkdownEditor.current = new Minimark(refTextEditor.current, refPreview.current, {
                formatInfoHandler: props.formatInfoHandler,
                beforePreview: (markdown: string) => props.beforePreview(markdown),
                textChanged: () => props.textChanged(refTextEditor.current?.value),
                setDirty: props.setDirty,
                suggestElement: (elementType: MarkdownElementType, selectionStart: number, selectionEnd: number, filterText?: string) => {
                    if (elementType) {
                        setMarkdownSuggestion({ elementType, selectionStart, selectionEnd, filterText });
                    }
                    else {
                        cancelSuggestion();
                    }
                },
                handleNavigationKey: (keyCode: KeyCode) => {
                    if (refMarkdownSuggestion.current?.filterText) {
                        if (keyCode === KeyCodes.down) {
                            refPersonPicker.current.focus();
                            return true;
                        }
                    }
                    return false;
                }
            },
                (html: string) => setPreviewHtml(html))
        }
    }, []);

    // In order to avoid stale closure as we aren't updating Markdown editor as we are instantiating Markdown editor only once
    useEffect(() => {
        refMarkdownEditor.current?.setBeforePreviewCallback((markdown: string) => props.beforePreview(markdown));
    }, [props.beforePreview]);

    // auto-size description box within specified limits
    useEffect(() => {
        if (refTextEditor.current) {
            refTextEditor.current.style.height = 'inherit';     // needed to handle deletions
            refTextEditor.current.style.height = `${Math.min(Math.max(refTextEditor.current.scrollHeight, textEditorMinHeightPx), textEditorMaxHeightPx)}px`;
        }
    }, [props.text, textEditorMinHeightPx, textEditorMinHeightPx]);

    const refreshPreview = useCallback(() => {
        refMarkdownEditor.current?.refresh();
    }, [refMarkdownEditor.current]);

    useEffect(() => {
        props.setRefresh?.(refreshPreview);
    }, [props.setRefresh, refreshPreview]);

    useEffect(() => {
        if (refMarkdownEditor.current) {
            refMarkdownEditor.current.refresh();
        }
    });

    const addFile = (): void => {
        setAttachImage(false);
        setShowFileUploadDialog(true);
    }

    const addImage = (): void => {
        setAttachImage(true);
        setShowFileUploadDialog(true);
    }

    const handleUploadedFiles = (files: UploadedFileInfo[]): void => {
        files.forEach(file => {
            let url = `${file.type === "image" ? "!" : ""}[${file.name}](${file.url})\n`;
            const currentLineText = getCurrentLineText(refTextEditor.current);
            if (currentLineText) {
                url = `\n${url}`;
            }
            refMarkdownEditor.current.insertText(
                url,
                { requirePreSpace: false, postString: null },
                (text: string | null, selectionStart: number, selectionEnd: number) => {
                    props.textChanged(text);
                }
            );
        });
        setShowFileUploadDialog(false);
        props.onFileAttached?.();

        // Attached images sometimes take a short while to load in preview
        setTimeout(() => {
            refreshPreview();
        }, 3000);
    }

    const handleMentionPerson = (person: Person): void => {
        refTextEditor.current.setSelectionRange(markdownSuggestion.selectionStart, markdownSuggestion.selectionEnd);
        refMarkdownEditor.current.insertText(
            `[${person.name}](${person.userInfoUrl})`,
            { requirePreSpace: false, postString: null },
            (text: string | null, selectionStart: number, selectionEnd: number) => {
                props.textChanged(text);
            }
        );
        cancelSuggestion();
    }

    const cancelSuggestion = (): void => {
        refMarkdownEditor.current.onSuggestCancelled();
        setMarkdownSuggestion(null);
    };

    useEffect(() => {
        const updatePersonPickerCoodrdinates = _.debounce(
            (textEditor: HTMLTextAreaElement, personPickerContainer: HTMLElement) => {
                if (!textEditor) {
                    return;
                }

                const caretCoords = getCaretCoordinates(textEditor, textEditor.selectionStart - 1);

                const ref = personPickerContainer;

                if (ref) {
                    ref.style.position = "absolute";
                    ref.style.zIndex = "1000";
                    ref.style.top = `${textEditor.offsetTop + caretCoords.top}px`;

                    let personPickerLeft = textEditor.offsetLeft + caretCoords.left;
                    if (caretCoords.left > textEditor.offsetWidth - personPickerWidthtPx) { // avoid popup from going beyond the screen on right
                        personPickerLeft = textEditor.offsetLeft + textEditor.offsetWidth - personPickerWidthtPx - 10;  // 10 => small amount of right margin to avoid border overlaps
                    }
                    ref.style.left = `${personPickerLeft}px`;
                }
            },
            300
        );

        updatePersonPickerCoodrdinates(refTextEditor.current, refPersonPickerContainer.current);
        return () => { resetMirrorDiv(refTextEditor.current) }
    }, [refTextEditor.current, refTextEditor.current?.selectionStart, refPersonPickerContainer.current, markdownSuggestion?.selectionStart]);

    useEffect(() => {
        // prevent scrollbar due to invisible mirror div when window's width is reduced
        const onResizeDebounced = _.debounce(
            () => {
                if (!refTextEditor.current) {
                    return;
                }

                getCaretCoordinates(refTextEditor.current, refTextEditor.current.selectionStart)
            },
            200
        );
        window.addEventListener('resize', onResizeDebounced)

        return () => window.removeEventListener('resize', onResizeDebounced);
    }, []);

    const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
        const files = e.clipboardData?.files;
        if (!files || files.length === 0) {
            return;
        }
        if (props.disabled || props.forbidAttachFiles || !(props.nodeTrackingId || props.nodeAnswerHubId)) {
            return;
        }

        // In some cases e.g. when text is copied from office apps the clipboard contains both the text version as well as an image of that text
        // Prevent the default onPaste handler in that case as it will lead to duplicate content
        e.preventDefault();
        e.stopPropagation();

        setCurrentActivity({
            state: UIOperationState.InProgress,
            message: "Uploading..."
        });

        uploadFiles(
            props.nodeTrackingId,
            props.nodeAnswerHubId,
            Array.from(e.clipboardData.files),
            true,
            (uploadedFileInfos: UploadedFileInfo[]) => {
                setCurrentActivity(null);
                handleUploadedFiles(uploadedFileInfos);
            },
            (error: any) => {
                setCurrentActivity({
                    state: UIOperationState.Failed,
                    message: `Upload failed: ${error.message ?? error}`
                });
            },
            null,
            true,
        );
    }

    if (isInReadOnlyMode) {
        return <div>Editing is disabled because website is in readonly mode.</div>
    }

    return <Wrapper className={props.className}>
        <Toolbar className="markdown-toolbar">
            <ToolbarButton icon="Bold" disabled={props.disabled} title={ResourceStrings.toolbarBold()} onClick={() => refMarkdownEditor.current.bold()} />
            <ToolbarButton icon="Italic" disabled={props.disabled} title={ResourceStrings.toolbarItalic()} onClick={() => refMarkdownEditor.current.italic()} />
            <span className="separator" />
            <ToolbarButton icon="Code" disabled={props.disabled} title={ResourceStrings.toolbarCode()} onClick={() => refMarkdownEditor.current.code()} />
            <ToolbarButton icon="Link" disabled={props.disabled} title={ResourceStrings.toolbarLink()} onClick={() => refMarkdownEditor.current.link()} />
            <ToolbarButton icon="RightDoubleQuote" disabled={props.disabled} title={ResourceStrings.toolbarQuote()} onClick={() => refMarkdownEditor.current.quote()} />
            <span className="separator" />
            <ToolbarButton
                icon="Photo2"
                title={
                    props.forbidAttachFiles && props.reasonCantAttachFiles
                        ? `${ResourceStrings.toolbarImage()} - ${props.reasonCantAttachFiles} `
                        : ResourceStrings.toolbarImage()}
                disabled={props.disabled || props.forbidAttachFiles || !(props.nodeTrackingId || props.nodeAnswerHubId)}
                onClick={() => addImage()}
            />
            <ToolbarButton
                icon="Attach"
                title={
                    props.forbidAttachFiles && props.reasonCantAttachFiles
                        ? `${ResourceStrings.toolbarFile()} - ${props.reasonCantAttachFiles} `
                        : ResourceStrings.toolbarFile()}
                disabled={props.disabled || props.forbidAttachFiles || !(props.nodeTrackingId || props.nodeAnswerHubId)}
                onClick={() => addFile()}
            />
            <span className="separator" />
            <ToolbarButton icon="Accounts" disabled={props.disabled} title="Mention someone" onClick={() => refMarkdownEditor.current.mentionPerson()} />
            <span className="separator" />
            <ToolbarButton icon="NumberedList" disabled={props.disabled} title={ResourceStrings.toolbarNumList()} onClick={() => refMarkdownEditor.current.numberList()} />
            <ToolbarButton icon="BulletedList" disabled={props.disabled} title={ResourceStrings.toolbarBulList()} onClick={() => refMarkdownEditor.current.bulletList()} />
            <ToolbarButton icon="Header3" disabled={props.disabled} title={ResourceStrings.toolbarHeading3()} onClick={() => refMarkdownEditor.current.heading3()} />
        </Toolbar>

        <textarea
            id="md-editor-1"
            aria-label={ResourceStrings.description()}
            className={`${props.textAreaClassName ?? ""} detail ${props.isInvalid ? "isInvalid" : ""}`}
            ref={refTextEditor}
            onPaste={(e) => onPaste(e)}
            value={props.text}
            readOnly={props.disabled}
            aria-required={props.ariaRequired}
            aria-describedby={props.ariaDescribedby ?? errorMesageId}
            placeholder={props.placeHolder} />

        {currentActivity && (currentActivity.state === UIOperationState.InProgress || currentActivity.state === UIOperationState.Failed) && (
            <div className="activity-indicator">
                {currentActivity.state === UIOperationState.InProgress && <Spinner size={SpinnerSize.small} />}
                <span className={currentActivity.state === UIOperationState.Failed ? "error" : ""}>&nbsp;{currentActivity.message}</span>
            </div>
        )}

        <div ref={refPersonPickerContainer} style={{ display: markdownSuggestion != null && markdownSuggestion.elementType === MarkdownElementType.Mention ? "block" : "none" }}>
            {markdownSuggestion != null && markdownSuggestion.elementType === MarkdownElementType.Mention && (
                <PersonPicker
                    ref={refPersonPicker}
                    filterText={markdownSuggestion.filterText}
                    widthPx={personPickerWidthtPx}
                    onItemSelected={handleMentionPerson}
                    onDismiss={() => {
                        cancelSuggestion();
                        refTextEditor.current.focus();
                    }}
                    relevantPersons={props.relevantPersons}
                    searchPersons={props.searchPersons}
                />
            )}
        </div>

        {props.notificationText && <div id={errorMesageId} className="reportDetail-messages">
            <Notification isInvalid={props.isInvalid}>{props.notificationText}</Notification>
        </div>}

        <HtmlPreview text={previewHtml} isPublic={false} style={showIf(!!props.text)} />

        <FileUploadDialog
            nodeTrackingId={props.nodeTrackingId}
            nodeAnswerHubId={props.nodeAnswerHubId}
            isOpen={showFileUploadDialog}
            attachImage={attachImage}
            approvedFileExtensions={approvedFileExtensionsCsv}
            onDismiss={() => setShowFileUploadDialog(false)}
            onUploaded={handleUploadedFiles}
        />
    </Wrapper >
}

export default forwardRef(MarkdownEditor);

interface ToolbarButtonProps {
    icon: string;
    title: string;
    disabled?: boolean;
    onClick?: () => void;
}

function ToolbarButton(props: ToolbarButtonProps): JSX.Element {
    const tooltipId = useId('tooltip');

    const tooltip = `${props.title} ${props.disabled ? "(disabled)" : ""}`;
    return <TooltipHost
        id={tooltipId}
        content={tooltip}
        directionalHint={DirectionalHint.topCenter}
    >
        <ToolbarButtonWrapper
            aria-describedby={tooltipId}
            aria-label={tooltip}
            disabled={props.disabled}
            onClick={() => props.onClick?.()}
        >
            <Icon iconName={props.icon} />
        </ToolbarButtonWrapper>
    </TooltipHost>
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;

    >* + * {
        margin-top: 2rem;
    }

    .reportDetail-messages {
        margin-top: .5rem;
        margin-left: 1rem;
        font-size: 1.4rem;
    }

    textarea {
        box-sizing: content-box;
        border: none;
        font-size: 1.6rem;
        padding: 1.5rem;
        margin-top: 0;
    }

    textarea.isInvalid {
        border: solid 1px var(--color-error);
    }

    .activity-indicator {
        display: flex;
        margin-top: 0;
        font-size: 1.2rem;
        padding: .2rem .5rem;
    }

    .html-preview {
        overflow: auto;
        max-height: 30rem;
        padding: 2rem 3rem;
        margin-top: 0.5rem;
        background-color: var(--color-background);
    }
`;

const Toolbar = styled.div`
    display: flex;
    padding: .4rem;
    flex-wrap: wrap;
    border-bottom: solid 1px #D9D9D6;

    .separator {
        margin: 0 0.8rem;
    }
`;

const ToolbarButtonWrapper = styled.button`
    border: none;
    background-color: transparent;
    padding: .8rem 1rem;
    margin-right: .3rem;
    cursor: pointer;
    color: var(--color-on-surface-link);

    :disabled {
        cursor: default;
        color: #888888;
    }

    :disabled:hover {
        background-color: transparent;
    }

    :hover {
        background-color: rgba(0,0,0,0.1);
    }
`;

const Notification = styled.div<{ isInvalid?: boolean }>`
    ${props => props.isInvalid && 'color: var(--color-error);'}
    padding: 0 1rem 1rem 0;
`;
